import { HeaderComponent } from './header-component.js';
import { onDocumentLoaded } from '@theme/utilities';

const SCROLL_TRANSPARENCY_THRESHOLD = 60;
const DYNAMIC_PDP_CTA_SELECTOR = '[data-dynamic-pdp-cta]';
const DYNAMIC_PDP_CTA_SIZE_TRANSITION_MS = 180;
const APP_MENU_LABEL_PATTERN = /\bapp\b/i;
const dynamicPdpCtaTimeouts = new WeakMap();

/**
 * @param {HTMLAnchorElement} link
 */
const isAppMenuLink = (link) => {
  return link.classList.contains('app-btn') || APP_MENU_LABEL_PATTERN.test(link.textContent ?? '');
};

/**
 * Adds the app redirect class to menu links labeled as app entry points.
 * @param {Element | null} container
 * @param {string} [appUrl]
 */
const syncAppMenuLinks = (container, appUrl) => {
  if (!container) return;

  const links = container.querySelectorAll('a');

  for (const link of links) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    if (!isAppMenuLink(link)) continue;

    link.classList.add('app-btn');
    if (appUrl) link.href = appUrl;
  }
};

/**
 * Header variant that always uses the drawer (hamburger) menu without a desktop overflow menu.
 *
 * @extends {HeaderComponent}
 */
class ImmortalsHeaderComponent extends HeaderComponent {
  requiredRefs = ['headerDrawerContainer', 'headerRowTop'];

  /**
   * RAF ID for threshold scroll handler throttling.
   * @type {number | null}
   */
  #scrollThresholdRafId = null;

  /**
   * Last scroll position used to determine threshold crossing direction.
   * @type {number}
   */
  #lastScrollThresholdTop = 0;

  #handleScrollThreshold = () => {
    if (this.#scrollThresholdRafId !== null) return;

    this.#scrollThresholdRafId = requestAnimationFrame(() => {
      this.#scrollThresholdRafId = null;
      this.#updateScrollThresholdState();
    });
  };

  #updateScrollThresholdState = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isPastThreshold = scrollTop >= SCROLL_TRANSPARENCY_THRESHOLD;
    const isScrollingDown = scrollTop > this.#lastScrollThresholdTop;

    this.toggleAttribute('data-scroll-threshold-transition', isPastThreshold && isScrollingDown);
    this.toggleAttribute('data-scrolled-past-threshold', isPastThreshold);
    this.#syncDynamicPdpCta(scrollTop);
    this.#lastScrollThresholdTop = scrollTop;
  };

  #syncInitialScrollThresholdState = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    this.#lastScrollThresholdTop = scrollTop;
    this.removeAttribute('data-scroll-threshold-transition');
    this.toggleAttribute('data-scrolled-past-threshold', scrollTop >= SCROLL_TRANSPARENCY_THRESHOLD);
    this.#syncDynamicPdpCta(scrollTop);
  };

  /**
   * Keeps the PDP header CTA copy, destination, and tracking metadata in sync with scroll state.
   * @param {number} scrollTop
   */
  #syncDynamicPdpCta = (scrollTop) => {
    const isScrolled = scrollTop > 0;
    const ctas = this.querySelectorAll(DYNAMIC_PDP_CTA_SELECTOR);

    for (const cta of ctas) {
      if (!(cta instanceof HTMLAnchorElement)) continue;

      const soldOut = cta.dataset.pdpSoldOut === 'true';
      const notifyActive = isScrolled && soldOut;
      const label = notifyActive
        ? (cta.dataset.dynamicPdpCtaNotifyLabel || 'Notify Me When Available')
        : (isScrolled ? cta.dataset.dynamicPdpCtaScrolledLabel : cta.dataset.dynamicPdpCtaInitialLabel);
      const href = notifyActive
        ? '#'
        : (isScrolled ? cta.dataset.dynamicPdpCtaScrolledHref : cta.dataset.dynamicPdpCtaInitialHref);
      const destination = notifyActive
        ? (cta.dataset.dynamicPdpCtaNotifyDestination || 'klaviyo:UXF7dX')
        : href;

      if (!label || !href) continue;

      this.#updateDynamicPdpCta(cta, label, href, destination, notifyActive);
    }
  };

  /**
   * Updates the CTA content and animates between intrinsic widths.
   * A same-page href is used while the sold-out notify state is active so the
   * click does not leave the page or count as an outbound link.
   * @param {HTMLAnchorElement} cta
   * @param {string} label
   * @param {string} href
   * @param {string} destination
   * @param {boolean} notifyActive
   */
  #updateDynamicPdpCta = (cta, label, href, destination, notifyActive) => {
    const nextDestination = destination || href;
    const isNotify = notifyActive === true;
    const currentLabel = cta.textContent?.trim();
    const currentDestination = cta.dataset.ctaDestination;
    const currentNotify = cta.dataset.pdpNotifyActive === 'true';

    if (currentLabel === label && currentDestination === nextDestination && currentNotify === isNotify) return;

    const applyCtaState = () => {
      cta.textContent = label;
      cta.href = href;
      cta.dataset.navLabel = label;
      cta.dataset.ctaLabel = label;
      cta.dataset.ctaDestination = nextDestination;
      cta.dataset.pdpNotifyActive = String(isNotify);

      if (isNotify) {
        if (cta.hasAttribute('target') && cta.dataset.dynamicPdpCtaTarget === undefined) {
          cta.dataset.dynamicPdpCtaTarget = cta.getAttribute('target') || '';
        }

        cta.removeAttribute('target');
        return;
      }

      if (cta.dataset.dynamicPdpCtaTarget) {
        cta.setAttribute('target', cta.dataset.dynamicPdpCtaTarget);
      }
    };

    const existingTimeoutId = dynamicPdpCtaTimeouts.get(cta);
    if (existingTimeoutId) {
      window.clearTimeout(existingTimeoutId);
    }

    const fromWidth = cta.getBoundingClientRect().width;

    cta.style.inlineSize = 'auto';
    applyCtaState();

    const toWidth = cta.getBoundingClientRect().width;

    if (fromWidth === 0 || toWidth === 0 || fromWidth === toWidth) {
      cta.style.inlineSize = '';
      return;
    }

    cta.style.inlineSize = `${fromWidth}px`;
    cta.getBoundingClientRect();

    requestAnimationFrame(() => {
      cta.style.inlineSize = `${toWidth}px`;
    });

    const timeoutId = window.setTimeout(() => {
      applyCtaState();
      cta.style.inlineSize = '';
      dynamicPdpCtaTimeouts.delete(cta);
    }, DYNAMIC_PDP_CTA_SIZE_TRANSITION_MS);
    dynamicPdpCtaTimeouts.set(cta, timeoutId);
  };

  #handlePdpStockChange = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#syncDynamicPdpCta(scrollTop);
  };

  /**
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const detail = event.detail || {};
    const variantId = detail.resource && detail.resource.id;
    const html = detail.data && detail.data.html;
    if (!html || typeof html.querySelector !== 'function') return;

    const configScript = html.querySelector('[data-pdp-cta-config]');
    if (!configScript) return;

    let config;

    try {
      config = JSON.parse(configScript.textContent || '');
    } catch (error) {
      return;
    }

    const eventProductId = detail.data && detail.data.productId;
    if (config.productId && eventProductId && String(eventProductId) !== String(config.productId)) return;

    const variantKey = variantId ? String(variantId) : '';
    let nextUrl = config.productUrl || '';

    if (config.hasMultipleVariants) {
      const variantUrl = variantKey && config.variantUrls ? config.variantUrls[variantKey] : '';
      nextUrl = variantUrl || config.productUrl || '';
    }

    const stock = variantKey && config.variantStock ? config.variantStock[variantKey] : null;
    const soldOut = Boolean(stock && stock.available === false && stock.inventoryPolicy !== 'continue');
    const ctas = this.querySelectorAll(DYNAMIC_PDP_CTA_SELECTOR);

    for (const cta of ctas) {
      if (!(cta instanceof HTMLAnchorElement)) continue;

      if (stock) cta.dataset.pdpSoldOut = String(soldOut);
      if (nextUrl) cta.dataset.dynamicPdpCtaScrolledHref = nextUrl;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#syncDynamicPdpCta(scrollTop);
  };

  connectedCallback() {
    super.connectedCallback();
    this.#syncInitialScrollThresholdState();
    document.addEventListener('scroll', this.#handleScrollThreshold);
    document.addEventListener('variant:update', this.#handleVariantUpdate);
    document.addEventListener('pdp:stock-change', this.#handlePdpStockChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('scroll', this.#handleScrollThreshold);
    document.removeEventListener('variant:update', this.#handleVariantUpdate);
    document.removeEventListener('pdp:stock-change', this.#handlePdpStockChange);

    if (this.#scrollThresholdRafId !== null) {
      cancelAnimationFrame(this.#scrollThresholdRafId);
      this.#scrollThresholdRafId = null;
    }
  }
}

if (!customElements.get('immortals-header-component')) {
  customElements.define('immortals-header-component', ImmortalsHeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component, immortals-header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }

  const appStoreUrl = 'https://apps.apple.com/us/app/immortals-bryan-johnson/id6761206137';
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.bryanjohnson.immortals';
  const fallbackUrl = 'https://apps.apple.com/us/app/immortals-bryan-johnson/id6761206137';

  const ua = navigator.userAgent || navigator.vendor || '';

  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const isAndroid = /android/i.test(ua);
  const appUrl = isIOS ? appStoreUrl : isAndroid ? playStoreUrl : fallbackUrl;
  const footers = document.querySelectorAll('footer');

  syncAppMenuLinks(header, appUrl);

  for (const footer of footers) {
    syncAppMenuLinks(footer, appUrl);
  }

  const ctas = document.querySelectorAll('.app-btn');

  for (const cta of ctas) {
    if (cta instanceof HTMLAnchorElement) {
      cta.href = appUrl;
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const cta = event.target instanceof Element ? event.target.closest('a') : null;

      if (cta instanceof HTMLAnchorElement && isAppMenuLink(cta)) {
        cta.href = appUrl;
      }
    },
    { capture: true }
  );
});
